/**
 * The platform credit, for the operator-facing app.
 *
 * It is chrome: it sits at the foot of the frame and never competes with the work above it. The
 * name and the link come from the one shared `platform` constant the public footer also reads, so
 * the two surfaces cannot drift and the credit changes in a single place.
 *
 * The owner opens in a new tab. Someone reading a credit is not leaving — they are curious about
 * who built this — and taking the tab away from a half-filled registration form to answer that is
 * how a desk loses work it cannot get back.
 *
 * `withLogos` is the front-door treatment. The sign-in screen is the one place in the app that is
 * a landing page rather than a working surface, so it can carry the owner's mark; every screen
 * behind it gets the quiet line, because a logo repeated above the payout sheet is noise.
 */
import { copyrightNotice, platform } from "@powwow/design-tokens";
import { cn } from "@powwow/ui";
import { useT } from "./i18n.js";
import { SITE_BASE_URL } from "./site-base.js";

export function PlatformFooter({
  className,
  withLogos = false,
}: {
  className?: string;
  withLogos?: boolean;
}) {
  // Read at render rather than baked into the bundle, so the year is right without a redeploy.
  const year = new Date().getFullYear();
  const t = useT();

  /**
   * The legal documents live on the public site, so these leave the app and open in a new tab —
   * the same rule the owner credit follows, and for the same reason: somebody checking what the
   * privacy policy says is in the middle of something here, and navigating away from a half-entered
   * score sheet to read it is a cost they did not agree to.
   */
  const legal = [
    { href: `${SITE_BASE_URL}/privacy`, label: t.t("site.legal.privacy") },
    { href: `${SITE_BASE_URL}/terms`, label: t.t("site.legal.terms") },
    { href: `${SITE_BASE_URL}/data-governance`, label: t.t("site.legal.dataGovernance") },
  ];

  return (
    <footer
      data-print="hide"
      className={cn("px-4 py-4 text-center text-caption text-muted lg:px-8", className)}
    >
      {withLogos && (
        <div className="mb-3 flex items-center justify-center">
          <a
            href={platform.owner.url}
            target="_blank"
            rel="noreferrer"
            /* The mark is supplied on white, so the chip is `paper` — the same white — and the
               ring rather than the fill is what separates it from the page behind. */
            className="block rounded-lg bg-paper px-4 py-2 shadow-sm ring-1 ring-line/60 transition hover:ring-accent focus-visible:ring-accent"
          >
            <img src={platform.owner.logo} alt={platform.owner.name} className="h-10 w-auto" />
          </a>
        </div>
      )}
      <nav className="mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {legal.map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            className="hover:text-accent hover:underline"
          >
            {link.label}
          </a>
        ))}
      </nav>
      {/* The mark above already carries the link, so the notice is plain text there — a second
          copy of the same link reads as clutter to a pointer and a screen reader alike. */}
      <p>
        {withLogos ? (
          copyrightNotice(year)
        ) : (
          <>
            {`© ${year} `}
            <a
              href={platform.owner.url}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-muted hover:text-accent hover:underline"
            >
              {platform.owner.name}
            </a>
          </>
        )}
      </p>
    </footer>
  );
}
